import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { createEvent } from "../../graphql/mutations";
import { getLeague } from "../../graphql/queries";
import { getLeagueFormats, getLeagueGameLabel } from "../../lib/leagueGames";
import { getLeagueScoring } from "../../lib/scoring";

const client = generateClient();

const CreateEvent: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const leagueId = searchParams.get("leagueId");

  const [formData, setFormData] = useState({
    name: "",
    format: "",
    date: "",
    time: "",
    location: "",
    description: "",
    maxRounds: 4,
    isPremium: false,
    hasTopCut: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: league, isLoading: leagueLoading } = useQuery({
    queryKey: ["league", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getLeague,
        variables: { id: leagueId! },
      });
      return result.data.getLeague;
    },
    enabled: !!leagueId,
  });

  const formats = getLeagueFormats(league?.game);

  useEffect(() => {
    if (league && !formData.format) {
      const leagueFormatIsValid = formats.includes(league.format);
      setFormData((prev) => ({
        ...prev,
        format: leagueFormatIsValid ? league.format : formats[0],
      }));
    }
  }, [league, formData.format, formats]);

  const handleInputChange = (
    field: string,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!leagueId) {
        throw new Error("League ID is required");
      }

      const eventData = {
        leagueId,
        name: formData.name,
        format: formData.format,
        date: formData.date,
        location: formData.location,
        description: formData.description,
        maxRounds: formData.maxRounds,
        isCompleted: false,
        isPremium: formData.isPremium || false,
        hasTopCut: formData.hasTopCut || false,
      };

      const result = await client.graphql({
        query: createEvent,
        variables: { input: eventData },
      });

      console.log("Event created:", result.data.createEvent);

      // Direct GraphQL writes bypass React Query's global mutation cache.
      await queryClient.invalidateQueries();
      navigate(`/admin/leagues/${leagueId}`);
    } catch (err: any) {
      console.error("Error creating event:", err);
      setError(err.message || "Failed to create event");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to League
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Create Event</h1>
          <p className="text-muted-foreground mt-2">
            Add a new {league ? getLeagueGameLabel(league.game) : "tournament"}{" "}
            event to the league
          </p>
        </div>
      </div>

      <div className="max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
            <CardDescription>
              Fill in the details for the new tournament event
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Event Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder={
                    league?.game === "RIFTBOUND"
                      ? "e.g., Riftbound League Night"
                      : league?.game === "DRAGON_BALL_MASTERS"
                      ? "e.g., Dragon Ball Masters League Night"
                      : "e.g., Standard Showdown, Modern Masters"
                  }
                  required
                />
              </div>

              {/* Format */}
              <div className="space-y-2">
                <Label htmlFor="format">Format *</Label>
                <Select
                  value={formData.format}
                  onValueChange={(value) => handleInputChange("format", value)}
                  disabled={leagueLoading}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {formats.map((format) => (
                      <SelectItem key={format} value={format}>
                        {format}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange("date", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleInputChange("time", e.target.value)}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  placeholder="e.g., Game Store A, 123 Main St"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Event description, prize support, etc."
                  rows={3}
                />
              </div>

              {/* Max Rounds */}
              <div className="space-y-2">
                <Label htmlFor="maxRounds">Max Rounds</Label>
                <Input
                  id="maxRounds"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.maxRounds}
                  onChange={(e) =>
                    handleInputChange("maxRounds", parseInt(e.target.value))
                  }
                />
              </div>

              {/* Premium Event */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    id="isPremium"
                    type="checkbox"
                    checked={formData.isPremium}
                    onChange={(e) =>
                      handleInputChange("isPremium", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="isPremium" className="text-sm font-medium">
                    Premium Event ({getLeagueScoring(league).premiumMultiplier}x
                    points multiplier)
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Premium events multiply match and placement points in league
                  standings
                </p>
              </div>

              {/* Top Cut Event */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    id="hasTopCut"
                    type="checkbox"
                    checked={formData.hasTopCut}
                    onChange={(e) =>
                      handleInputChange("hasTopCut", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <Label htmlFor="hasTopCut" className="text-sm font-medium">
                    Has Top Cut
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  This event will have a top cut phase with bonus points for
                  final placements
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/admin/leagues/${leagueId}`)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create Event"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateEvent;
